// drugsManagement.js — the admin's pharmacy price list and stock desk.
//
// This module had NO owner in the audit and was the last place still treating
// `drugs.current_stock` as the truth. It read or wrote that column in seven places,
// including an `.update({ current_stock })` — a read-modify-write off a value another
// tab may already have changed (audit C2: two cashiers sold 5 units from a stock of
// 10 and the column ended at 8, three units conjured out of nothing).
//
// After migration 0007 NO client role may write that table's stock at all, and 0008
// DROPS the column outright. Every one of those seven reads would have rendered
// `undefined`, and the update would have failed. What replaces them:
//
//  • STOCK IS A FOLD (spec §4.1, invariant 1). `money/stock-fold.js` folds the same
//    ledger events the dashboard and the cashier screens fold, so no two screens can
//    disagree about how many boxes are on the shelf.
//
//  • A NEGATIVE POSITION IS SHOWN AS NEGATIVE AND FLAGGED, never clamped. If two
//    devices sell the last unit offline, five boxes have physically left the shelf:
//    the books are wrong, the shelf is not (spec §4). Clamping to zero would invent
//    stock; refusing the sale is the dropped `current_stock >= 0` CHECK, which turned
//    a constraint violation into lost cash.
//
//  • RESTOCKING IS A `record_restock` EVENT and a manual correction is a
//    `record_stock_adjustment` (reason REQUIRED). Both are admin-only, online-only
//    RPCs through the single validation path (spec §6). Neither is ever queued.
//
//  • DELETION IS DEACTIVATION (`is_active = false`, spec §3.2). The ledger names a
//    drug inside `items` jsonb, where no foreign key can protect it; a hard delete
//    would make its position vanish silently from `v_stock_positions` — inventory
//    disappearing with no error and no trace. A deactivated drug stays in the fold
//    and stays resolvable by name on a receipt printed two years ago.
//
//  • PRICES ARE INTEGERS (migration 0009, invariant 5). `parseFloat` is gone: XAF has
//    no subunit, and two decimal places are audit M2 — three lines of 33.33 printing
//    as 33+33+33 under a total of 100.
//
//  • NAMES ARE ESCAPED and row actions are bound with `addEventListener`, not
//    `onclick="…('${drug.name}')"` with a hand-rolled quote escape (audit M7).

import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency } from './utils.js';

import { db } from './db/schema.js';
import { getAllEvents, fetchAllPages } from './db/index.js';
import { getEngine } from './ui/engine-boot.js';
import { messageFor, messageForError } from './ui/engine-messages.js';
import { foldStock, dedupeById } from './money/index.js';

/** Low-stock threshold in units. Same figure as the dashboard uses. */
const LOW_STOCK_THRESHOLD = 20;

/**
 * Escape text destined for an `innerHTML` template (audit M7).
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Parse a whole non-negative FCFA amount, or `null`. Never `parseFloat`.
 * @param {*} raw
 * @returns {number|null}
 */
function parseWholeFcfa(raw) {
    const text = String(raw == null ? '' : raw).trim();
    if (!/^\d{1,9}$/.test(text)) return null;
    const n = Number(text);
    return Number.isSafeInteger(n) ? n : null;
}

/**
 * Parse a whole quantity, optionally signed. `null` when it is not an integer.
 * @param {*} raw
 * @param {boolean} allowNegative
 * @returns {number|null}
 */
function parseWholeQty(raw, allowNegative) {
    const text = String(raw == null ? '' : raw).trim();
    const pattern = allowNegative ? /^-?\d{1,9}$/ : /^\d{1,9}$/;
    if (!pattern.test(text)) return null;
    const n = Number(text);
    return Number.isSafeInteger(n) ? n : null;
}

/** Stock movements are online-only (spec §6). */
const ONLINE_ONLY =
    'Les mouvements de stock exigent une connexion au serveur : ils ne sont jamais mis '
    + 'en file d\'attente. Rétablissez le réseau, puis recommencez.';

export const DrugsManagement = {
    elements: {},
    /** @type {object[]} the price list */
    drugs: [],
    /** @type {Object<string, number>} drug id → folded position */
    positions: {},
    /** @type {object[]} negative positions and unusable rows, from the fold */
    anomalies: [],
    /** Current toolbar search text; renderDrugs filters on it. */
    searchTerm: '',

    init() {
        this.elements = {
            modal: document.getElementById('drugs-modal'),
            manageBtn: document.getElementById('manage-drugs-btn'),
            closeModalBtn: document.getElementById('close-drugs-modal'),
            addBtn: document.getElementById('show-add-drug-form-btn'),
            form: document.getElementById('drug-form'),
            formSection: document.getElementById('drug-form-section'),
            formTitle: document.getElementById('drug-form-title'),
            cancelFormBtn: document.getElementById('cancel-drug-form'),
            tableBody: document.getElementById('drugs-table-body'),
            stockNotice: document.getElementById('drugs-stock-notice'),
            searchInput: document.getElementById('drugs-search'),
            countLabel: document.getElementById('drugs-count'),
            // Form inputs
            idInput: document.getElementById('drug-id'),
            nameInput: document.getElementById('drug-name'),
            descriptionInput: document.getElementById('drug-description'),
            priceInput: document.getElementById('drug-price'),
            stockInput: document.getElementById('drug-stock'),
            stockRow: document.getElementById('drug-stock-row'),
            unitInput: document.getElementById('drug-unit'),
            // Stock movement form
            movementSection: document.getElementById('stock-movement-section'),
            movementForm: document.getElementById('stock-movement-form'),
            movementTitle: document.getElementById('stock-movement-title'),
            movementDrugId: document.getElementById('movement-drug-id'),
            movementDrugName: document.getElementById('movement-drug-name'),
            movementKind: document.getElementById('movement-kind'),
            movementQuantity: document.getElementById('movement-quantity'),
            movementReason: document.getElementById('movement-reason'),
            movementHint: document.getElementById('movement-hint'),
            cancelMovementBtn: document.getElementById('cancel-stock-movement')
        };

        this.setupEventListeners();
        console.log('DrugsManagement module initialized');
    },

    setupEventListeners() {
        this.elements.manageBtn?.addEventListener('click', () => this.openModal());
        this.elements.closeModalBtn?.addEventListener('click', () => this.closeModal());
        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });

        this.elements.addBtn?.addEventListener('click', () => this.showForm());
        this.elements.cancelFormBtn?.addEventListener('click', () => this.hideForm());
        this.elements.form?.addEventListener('submit', (e) => this.handleFormSubmit(e));

        this.elements.searchInput?.addEventListener('input', () => {
            this.searchTerm = this.elements.searchInput.value;
            this.renderDrugs();
        });

        this.elements.cancelMovementBtn?.addEventListener('click', () => this.hideMovementForm());
        this.elements.movementForm?.addEventListener('submit', (e) => this.handleMovementSubmit(e));
        this.elements.movementKind?.addEventListener('change', () => this.renderMovementHint());

        // Delegated row actions: no drug name is ever interpolated into an
        // attribute, so `Paracétamol (500mg) d'Ombessa` cannot break out of one.
        this.elements.tableBody?.addEventListener('click', (e) => {
            const button = e.target instanceof Element ? e.target.closest('button[data-action]') : null;
            if (!button) return;
            const id = button.getAttribute('data-drug-id');
            switch (button.getAttribute('data-action')) {
                case 'edit': this.editDrug(id); break;
                case 'restock': this.showMovementForm(id, 'restock'); break;
                case 'adjust': this.showMovementForm(id, 'stock_adjustment'); break;
                case 'deactivate': void this.setActive(id, false); break;
                case 'reactivate': void this.setActive(id, true); break;
            }
        });
    },

    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        await this.loadDrugs();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
        this.hideForm();
        this.hideMovementForm();
    },

    // ---- price-list form ---------------------------------------------------

    /**
     * @param {object|null} drug
     */
    showForm(drug = null) {
        this.elements.formSection?.classList.remove('hidden');
        this.hideMovementForm();

        if (drug) {
            this.elements.formTitle.textContent = 'Modifier un médicament';
            this.elements.idInput.value = drug.id;
            this.elements.nameInput.value = drug.name;
            this.elements.descriptionInput.value = drug.description || '';
            this.elements.priceInput.value = String(drug.price ?? '');
            this.elements.unitInput.value = drug.unit || '';
            // Stock is NOT editable on an existing drug. It is a fold of events; the
            // only way to change it is to record one (restock / adjustment), which is
            // exactly what makes every movement attributable and reversible.
            this.toggle(this.elements.stockRow, false);
            if (this.elements.stockInput) this.elements.stockInput.value = '0';
        } else {
            this.elements.formTitle.textContent = 'Ajouter un médicament';
            this.elements.form.reset();
            this.elements.idInput.value = '';
            this.toggle(this.elements.stockRow, true);
            if (this.elements.stockInput) this.elements.stockInput.value = '0';
        }
    },

    hideForm() {
        this.elements.formSection?.classList.add('hidden');
        this.elements.form?.reset();
    },

    /**
     * @param {Element|null|undefined} el
     * @param {boolean} visible
     */
    toggle(el, visible) {
        if (!el) return;
        el.classList.toggle('hidden', !visible);
    },

    // ---- loading -----------------------------------------------------------

    /**
     * Load the price list (paginated) and fold the ledger for the positions.
     *
     * The two are separate on purpose: `drugs` is the MENU (spec §5), the ledger is
     * the record of what moved. Joining them into one "stock" column is precisely
     * the mistake `current_stock` was.
     */
    async loadDrugs() {
        if (!this.elements.tableBody) return;
        this.elements.tableBody.innerHTML =
            '<tr><td colspan="5" class="text-center text-muted">Chargement des médicaments…</td></tr>';

        try {
            const drugs = await fetchAllPages((from, to) => supabase
                .from('drugs')
                .select('*')
                .order('name', { ascending: true })
                .order('id', { ascending: true })
                .range(from, to));
            this.drugs = drugs;
            // Cache for the offline screens, exactly as the rest of the app does.
            try { await db.drugs.bulkPut(drugs); } catch { /* cache only */ }

            const events = await this.loadEvents();
            const fold = foldStock(events);
            this.positions = fold.positions;
            this.anomalies = fold.anomalies;

            this.renderDrugs();
            this.renderStockNotice();
        } catch (error) {
            console.error('Chargement des médicaments impossible:', error);
            showStatus(`Chargement des médicaments impossible : ${error.message}`, true);
            this.elements.tableBody.innerHTML =
                '<tr><td colspan="5" class="text-center" style="color: var(--danger-600);">Erreur de chargement</td></tr>';
        }
    },

    /**
     * Every ledger event, server copy merged with this device's mirror.
     *
     * NOT date-filtered and NOT kind-filtered: a drug's position is the fold of its
     * ENTIRE history (spec §8) and a `void` restores stock from its target sale's
     * lines, so dropping either side of that pair silently loses inventory.
     * @returns {Promise<object[]>}
     */
    async loadEvents() {
        const engine = getEngine();
        const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
        let serverEvents = [];
        if (engine && engine.transport && online) {
            try {
                serverEvents = await engine.transport.fetchAllLedger();
            } catch (error) {
                console.warn('Registre serveur injoignable, repli sur le miroir local:', error);
            }
        }
        const localEvents = await getAllEvents(db).catch(() => []);
        return dedupeById(serverEvents.concat(localEvents));
    },

    renderDrugs() {
        const body = this.elements.tableBody;
        if (!body) return;
        body.innerHTML = '';

        // Substring filter over name and presentation — plain `includes`, no
        // `new RegExp` on user input anywhere in this file (audit M6). With the
        // real catalogue at 240 rows, an inventory without search is a scroll
        // hunt; the cashier screens have had one from the start.
        const term = String(this.searchTerm || '').trim().toLowerCase();
        const shown = (term
            ? this.drugs.filter(d =>
                String(d.name || '').toLowerCase().includes(term)
                || String(d.unit || '').toLowerCase().includes(term))
            : this.drugs.slice())
            .sort((a, b) => String(a.name).localeCompare(String(b.name), 'fr'));

        if (this.elements.countLabel) {
            const active = this.drugs.filter(d => d.is_active !== false).length;
            this.elements.countLabel.textContent = term
                ? `${shown.length} / ${this.drugs.length}`
                : `${this.drugs.length} médicaments · ${active} actifs`;
        }

        if (shown.length === 0) {
            // The empty state keeps the table and says why it is empty (an
            // element that vanishes reads as a bug, not as « no match »).
            const message = term
                ? `Aucun médicament ne correspond à « ${term} ».`
                : 'Aucun médicament';
            body.innerHTML = `<tr><td colspan="5" class="text-center text-muted">${escapeHtml(message)}</td></tr>`;
            return;
        }

        for (const drug of shown) {
            const position = Number.isInteger(this.positions[drug.id]) ? this.positions[drug.id] : 0;
            const isActive = drug.is_active !== false;

            // NEGATIVE IS SHOWN AS NEGATIVE. Never `Math.max(0, …)`.
            let stockState = 'stock-ok';
            let stockWarning = '';
            if (position < 0) {
                stockState = 'stock-out';
                stockWarning = 'Position négative : vente hors ligne au-delà du stock connu. À réconcilier.';
            } else if (position === 0) {
                stockState = 'stock-out';
            } else if (position < LOW_STOCK_THRESHOLD) {
                stockState = 'stock-low';
            }

            // Two columns are deliberately absent. Unité: the position pill
            // already prints « 12 CP SPE » word for word. Description: none of
            // the 235 real drugs carries one, and with 5 columns the table FITS
            // the modal — with 7, Statut and Actions were clipped off-screen.
            // Both fields stay editable in the « Modifier » form.
            const tr = document.createElement('tr');
            if (!isActive) tr.className = 'drug-row--inactive';
            tr.innerHTML = `
                <td class="font-medium">${escapeHtml(drug.name)}</td>
                <td class="text-right font-medium">${escapeHtml(formatCurrency(drug.price))}</td>
                <td>
                    <span class="stock-cell ${stockState}"><span class="stock-dot"></span>${escapeHtml(`${position} ${drug.unit || 'unités'}`)}</span>
                    ${stockWarning ? `<div class="text-xs" style="color: var(--danger-600);">${escapeHtml(stockWarning)}</div>` : ''}
                </td>
                <td>${isActive
                    ? '<span class="status-text">Actif</span>'
                    : '<span class="status-text status-text--off">Désactivé</span>'}</td>
                <td></td>`;

            // Inside .table-actions, a flex row that never wraps. Appended bare
            // into the <td>, the four buttons stacked into four lines and every
            // row grew ~200 px tall.
            const actions = document.createElement('div');
            actions.className = 'table-actions';
            actions.appendChild(this.actionButton('edit', drug.id, 'Modifier', 'btn btn-secondary btn-sm'));
            // « Réappro » : le libellé long poussait la colonne Actions hors du
            // modal ; le formulaire qu'il ouvre dit « Réapprovisionnement » en toutes lettres.
            actions.appendChild(this.actionButton('restock', drug.id, 'Réappro', 'btn btn-primary btn-sm'));
            actions.appendChild(this.actionButton('adjust', drug.id, 'Ajuster', 'btn btn-warning btn-sm'));
            actions.appendChild(isActive
                ? this.actionButton('deactivate', drug.id, 'Désactiver', 'btn btn-danger btn-sm')
                : this.actionButton('reactivate', drug.id, 'Réactiver', 'btn btn-success btn-sm'));
            tr.lastElementChild.appendChild(actions);
            body.appendChild(tr);
        }
    },

    /**
     * @param {string} action
     * @param {string} drugId
     * @param {string} label
     * @param {string} className
     * @returns {HTMLButtonElement}
     */
    actionButton(action, drugId, label, className) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = label;
        button.setAttribute('data-action', action);
        button.setAttribute('data-drug-id', drugId);
        return button;
    },

    /**
     * Surface what the fold could not use, and every negative position.
     *
     * `anomalies[]` is not diagnostics — it is the pharmacy's daily check, the
     * equivalent of the drawer's day-close variance (spec §4.4). A line the fold
     * cannot attribute to a drug is inventory quietly leaving the building.
     */
    renderStockNotice() {
        const el = this.elements.stockNotice;
        if (!el) return;

        const negatives = this.anomalies.filter(a => a.reason === 'negative-position');
        const unusable = this.anomalies.filter(a => a.reason !== 'negative-position');
        const parts = [];
        if (negatives.length > 0) {
            parts.push(`${negatives.length} médicament(s) en position négative — `
                + 'vente hors ligne au-delà du stock connu. Enregistrez un réapprovisionnement '
                + 'ou un ajustement motivé après comptage physique.');
        }
        if (unusable.length > 0) {
            parts.push(`${unusable.length} ligne(s) d'événement inexploitables par le calcul de stock `
                + '(article non identifié ou quantité invalide) — prévenez l\'administrateur.');
        }
        el.textContent = parts.join(' ');
        el.classList.toggle('hidden', parts.length === 0);
    },

    // ---- create / update ---------------------------------------------------

    /**
     * @param {Event} e
     */
    async handleFormSubmit(e) {
        e.preventDefault();

        const id = this.elements.idInput.value;
        const name = this.elements.nameInput.value.trim();
        const description = this.elements.descriptionInput.value.trim();
        const price = parseWholeFcfa(this.elements.priceInput.value);
        const unit = this.elements.unitInput.value.trim();

        if (!name) {
            showStatus('Le nom du médicament est obligatoire.', true);
            return;
        }
        if (price === null) {
            // Migration 0009 made these columns `int check (>= 0)`. A decimal here
            // would be rounded by the cast — a wrong receipt every day until someone
            // notices (audit M2). Refuse instead.
            showStatus('Le prix doit être un nombre entier de FCFA, sans décimale.', true);
            return;
        }

        if (id) {
            await this.updateDrug(id, { name, description, price, unit });
            return;
        }

        const openingStock = parseWholeQty(this.elements.stockInput?.value, false);
        if (openingStock === null) {
            showStatus('Le stock initial doit être un nombre entier positif ou nul.', true);
            return;
        }
        await this.createDrug({ name, description, price, unit }, openingStock);
    },

    /**
     * Create a drug and, when an opening quantity was given, record it as a
     * `restock` EVENT rather than as a column value.
     *
     * The two steps are reported separately and honestly: if the drug is created and
     * the opening stock is refused, the admin is told exactly that. Announcing a
     * partial success as a success is the failure mode this rewrite exists to end
     * (audit C3: « Vente complétée avec succès ! » for a sale whose stock decrement
     * had failed).
     *
     * @param {{name:string, description:string, price:number, unit:string}} data
     * @param {number} openingStock
     */
    async createDrug(data, openingStock) {
        try {
            showStatus('Création du médicament…', false);
            const { data: inserted, error } = await supabase
                .from('drugs')
                .insert([data])
                .select('id, name, price, unit')
                .single();
            if (error) throw error;

            this.hideForm();

            if (openingStock > 0) {
                const result = await this.recordMovement('restock', inserted, openingStock, null);
                if (!result.ok) {
                    await this.refreshAll();
                    showStatus(
                        `Médicament « ${data.name} » créé, MAIS le stock initial n'a pas été enregistré : `
                        + result.message, true);
                    return;
                }
            }

            await this.refreshAll();
            showStatus(openingStock > 0
                ? `Médicament créé et stock initial de ${openingStock} enregistré.`
                : 'Médicament créé.', false);
        } catch (error) {
            console.error('Création du médicament impossible:', error);
            showStatus(`Création impossible : ${error.message}`, true);
        }
    },

    /**
     * @param {string} id
     * @param {{name:string, description:string, price:number, unit:string}} data
     */
    async updateDrug(id, data) {
        try {
            showStatus('Mise à jour du médicament…', false);
            // NOTE: no `current_stock` in this payload, and there can never be one
            // again — the column is dropped and the table's UPDATE policy is
            // admin-only (0007), which also closes audit H8 where `cashier_b` held
            // UPDATE on the whole row and could rewrite prices.
            const { error } = await supabase.from('drugs').update(data).eq('id', id);
            if (error) throw error;

            this.hideForm();
            await this.refreshAll();
            showStatus('Médicament mis à jour.', false);
        } catch (error) {
            console.error('Mise à jour du médicament impossible:', error);
            showStatus(`Mise à jour impossible : ${error.message}`, true);
        }
    },

    /**
     * @param {string|null} id
     */
    editDrug(id) {
        const drug = this.drugs.find(d => d.id === id);
        if (!drug) {
            showStatus('Ce médicament n\'est plus dans la liste chargée. Rechargez.', true);
            return;
        }
        this.showForm(drug);
    },

    // ---- deactivate / reactivate -------------------------------------------

    /**
     * Removal is DEACTIVATION, never DELETE (spec §3.2).
     *
     * Two independent locks stand behind this: no client role holds DELETE on
     * `drugs` (0007), and a `BEFORE DELETE` guard refuses when the row's id appears
     * on any `money_ledger.items` line (0009). That guard raises **`23503`** — the
     * same errcode the removed `ON DELETE RESTRICT` foreign key produced — precisely
     * so the existing client handling keeps working. It is preserved below.
     *
     * VERIFIED, AND WORTH SAYING OUT LOUD: this screen no longer issues a DELETE at
     * all, so `23503` cannot arise from this path any more. The handler is kept as
     * the correct response should any future path attempt one, and because a
     * disappearing error branch is how a protection quietly stops being tested.
     *
     * @param {string|null} id
     * @param {boolean} active
     */
    async setActive(id, active) {
        const drug = this.drugs.find(d => d.id === id);
        if (!drug) return;

        if (!active) {
            const position = Number.isInteger(this.positions[id]) ? this.positions[id] : 0;
            const warning = position !== 0
                ? `\n\nCe médicament porte encore une position de ${position}. `
                    + 'Elle restera visible dans le stock et dans les rapports : '
                    + 'un article désactivé ne disparaît jamais du calcul.'
                : '';
            if (!confirm(
                `Désactiver « ${drug.name} » ?\n\n`
                + 'Il ne sera plus proposé à la vente, mais son historique et sa position de stock '
                + 'restent intacts et son nom reste lisible sur les anciens reçus.' + warning)) {
                return;
            }
        }

        try {
            showStatus(active ? 'Réactivation…' : 'Désactivation…', false);
            const { error } = await supabase.from('drugs').update({ is_active: active }).eq('id', id);
            if (error) {
                if (error.code === '23503') {
                    // Kept from the pre-rewrite code, which the audit credits as one
                    // of the things it did well.
                    throw new Error(
                        'Ce médicament figure sur des lignes du registre et ne peut pas être supprimé — '
                        + 'désactivez-le à la place.');
                }
                throw error;
            }
            await this.refreshAll();
            showStatus(active ? 'Médicament réactivé.' : 'Médicament désactivé.', false);
        } catch (error) {
            console.error('Changement de statut impossible:', error);
            showStatus(`Opération impossible : ${error.message}`, true);
        }
    },

    // ---- stock movements ---------------------------------------------------

    /**
     * @param {string|null} drugId
     * @param {'restock'|'stock_adjustment'} kind
     */
    showMovementForm(drugId, kind) {
        const drug = this.drugs.find(d => d.id === drugId);
        if (!drug) return;

        this.hideForm();
        this.elements.movementSection?.classList.remove('hidden');
        this.elements.movementForm?.reset();

        if (this.elements.movementDrugId) this.elements.movementDrugId.value = drug.id;
        if (this.elements.movementDrugName) this.elements.movementDrugName.value = drug.name;
        if (this.elements.movementKind) this.elements.movementKind.value = kind;
        if (this.elements.movementTitle) {
            this.elements.movementTitle.textContent = kind === 'restock'
                ? 'Réapprovisionnement' : 'Ajustement de stock';
        }
        this.renderMovementHint();
    },

    hideMovementForm() {
        this.elements.movementSection?.classList.add('hidden');
        this.elements.movementForm?.reset();
    },

    renderMovementHint() {
        const el = this.elements.movementHint;
        if (!el) return;
        const kind = this.elements.movementKind?.value;
        const drug = this.drugs.find(d => d.id === this.elements.movementDrugId?.value);
        const position = drug && Number.isInteger(this.positions[drug.id]) ? this.positions[drug.id] : 0;

        el.textContent = kind === 'restock'
            ? `Position actuelle : ${position}. Une quantité positive uniquement. `
                + 'Le motif est facultatif pour un réapprovisionnement.'
            : `Position actuelle : ${position}. La quantité peut être négative (perte, péremption, `
                + 'correction après comptage). Le motif est OBLIGATOIRE.';
    },

    /**
     * @param {Event} e
     */
    async handleMovementSubmit(e) {
        e.preventDefault();

        const drugId = this.elements.movementDrugId?.value;
        const drug = this.drugs.find(d => d.id === drugId);
        if (!drug) {
            showStatus('Médicament introuvable — rechargez la liste.', true);
            return;
        }

        const kind = this.elements.movementKind?.value === 'stock_adjustment'
            ? 'stock_adjustment' : 'restock';
        const qty = parseWholeQty(this.elements.movementQuantity?.value, kind === 'stock_adjustment');
        const reason = (this.elements.movementReason?.value || '').trim();

        if (qty === null || qty === 0) {
            showStatus(kind === 'restock'
                ? 'La quantité doit être un nombre entier strictement positif.'
                : 'La quantité doit être un nombre entier non nul (positive ou négative).', true);
            return;
        }
        if (kind === 'restock' && qty < 0) {
            showStatus('Un réapprovisionnement ne peut pas être négatif. Utilisez « Ajuster » pour une correction à la baisse.', true);
            return;
        }
        if (kind === 'stock_adjustment' && !reason) {
            showStatus(messageFor('stock-adjustment-missing-reason'), true);
            return;
        }
        if (reason.length > 500) {
            showStatus(messageFor('reason-too-long'), true);
            return;
        }

        const submit = this.elements.movementForm?.querySelector('button[type="submit"]');
        if (submit) submit.disabled = true;
        try {
            const result = await this.recordMovement(kind, drug, qty, reason || null);
            if (!result.ok) {
                showStatus(result.message, true);
                return;
            }
            this.hideMovementForm();
            await this.refreshAll();
            showStatus(kind === 'restock'
                ? `Réapprovisionnement enregistré : +${qty} ${drug.unit || 'unités'} de « ${drug.name} ».`
                : `Ajustement enregistré : ${qty > 0 ? '+' : ''}${qty} ${drug.unit || 'unités'} de « ${drug.name} ».`,
                false);
        } finally {
            if (submit) submit.disabled = false;
        }
    },

    /**
     * Send one stock event through the single validation path.
     *
     * `amount` on a `restock` / `stock_adjustment` is a BOOKKEEPING value, not cash:
     * `money-fold.js` excludes both kinds from every cash total and surfaces them in
     * `ignored[]` (spec §3.1), and `v_daily_cash` reports them as
     * `ignored_event_count` / `ignored_amount`. It is the inventory value of the
     * movement, and the server requires it to be a POSITIVE integer — which is why a
     * movement of a zero-priced drug is refused here, with a message that says what
     * to fix, rather than parked as `amount-must-be-positive-integer`.
     *
     * @param {'restock'|'stock_adjustment'} kind
     * @param {{id:string, name:string, price:*, unit?:string}} drug
     * @param {number} qty Signed for `stock_adjustment`; positive for `restock`.
     * @param {string|null} reason
     * @returns {Promise<{ok:boolean, message?:string}>}
     */
    async recordMovement(kind, drug, qty, reason) {
        const engine = getEngine();
        if (!engine || !engine.transport) return { ok: false, message: messageFor('engine-not-ready') };
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return { ok: false, message: ONLINE_ONLY };
        }

        const unitPrice = parseWholeFcfa(drug.price);
        if (unitPrice === null) {
            return {
                ok: false,
                message: `Le prix de « ${drug.name} » n'est pas un nombre entier de FCFA. `
                    + 'Corrigez-le avant d\'enregistrer un mouvement de stock.'
            };
        }
        const lineTotal = Math.abs(qty) * unitPrice;
        if (lineTotal <= 0) {
            return {
                ok: false,
                message: `La valeur du mouvement est nulle car « ${drug.name} » est au prix de 0 FCFA. `
                    + 'Renseignez son prix, puis enregistrez le mouvement.'
            };
        }

        const event = {
            id: crypto.randomUUID(),
            // Positive bookkeeping value; the SIGN of the movement lives in `qty`.
            amount: lineTotal,
            reason,
            items: [{
                item_id: drug.id,
                item_kind: 'drug',
                name: drug.name,
                qty,
                unit_price: unitPrice,
                line_total: lineTotal
            }],
            device_time: new Date().toISOString()
        };
        if (engine.deviceId) event.device_id = engine.deviceId;

        try {
            const verdict = kind === 'restock'
                ? await engine.transport.recordRestock(event)
                : await engine.transport.recordStockAdjustment(event);
            const status = verdict && verdict.status;
            if (status === 'inserted' || status === 'already_present') return { ok: true };
            return { ok: false, message: messageFor(verdict && verdict.reason ? verdict.reason : 'conflict') };
        } catch (error) {
            console.error('Mouvement de stock refusé:', error);
            return { ok: false, message: messageForError(error) };
        }
    },

    /**
     * Pull the new event back into this device's mirror, then repaint every screen
     * that shows a position, so no two of them can disagree (audit H2).
     */
    async refreshAll() {
        const engine = getEngine();
        if (engine) {
            try { await engine.kick(); } catch (error) { console.warn('Synchronisation différée:', error); }
        }
        try { await this.loadDrugs(); } catch (error) { console.warn('Rechargement différé:', error); }
        try {
            if (window.AppCore && window.AppCore.refreshDrugs) await window.AppCore.refreshDrugs();
        } catch (error) {
            console.warn('Rafraîchissement du tableau de bord différé:', error);
        }
    }
};

// Kept for compatibility. Nothing in `index.html` calls into it: every row action is
// bound with `addEventListener` and a `data-drug-id` (audit M7).
window.DrugsManagement = DrugsManagement;
